'use client';

import { useEffect, useRef, useState, useSyncExternalStore, type FormEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { HiMicrophone, HiPause, HiPlay, HiSpeakerWave, HiStop, HiXMark, HiPaperAirplane } from 'react-icons/hi2';
import { LanguageType, Locale } from 'shared/src/types/languages.type';
import { articleAdminUIContent } from '@/lib/ui-content/article-ui-content';
import { Narrator, SERVER_SNAPSHOT, type NarratorSnapshot } from '@/lib/narrator/narrator';
import { askReader } from '@/lib/narrator/hosted-voice';

interface ArticleNarratorProps {
  articleId: string;
  lang: LanguageType;
  locale: Locale;
  title: string;
  description: string;
}

/**
 * The article's reader: a "Listen" control in the article's stats row and,
 * while it reads, a bar at the foot of the screen with a question field and
 * a hold-to-talk microphone. Everything spoken comes from
 * lib/narrator/narrator.ts; this is the view. Renders nothing when the
 * language can be read neither by the rendered voice nor by the device.
 */
export function ArticleNarrator({ articleId, lang, locale, title, description }: ArticleNarratorProps) {
  const ui = articleAdminUIContent[lang];

  // One engine per article view. Its constructor touches nothing; attach()
  // in the effect below finds the voices, so StrictMode's double render and
  // the server render cost nothing.
  const [narrator] = useState(
    () =>
      new Narrator({
        articleId,
        title,
        lede: description,
        language: lang,
        locale,
        lines: {
          intro: ui.narratorIntro,
          backToArticle: ui.narratorBackToArticle,
          end: ui.narratorEnd,
          answerFailed: ui.narratorAnswerFailed,
          searching: ui.narratorSearching,
          stillSearching: ui.narratorStillSearching,
        },
        storageUrl: process.env.NEXT_PUBLIC_SUPABASE_URL ?? null,
        getRoot: () => document.querySelector('article'),
        ask: (request, { voice }) => askReader({ articleId, lang, locale, ...request, voice }),
      }),
  );

  useEffect(() => {
    narrator.attach();
    return () => narrator.detach();
  }, [narrator]);

  const snapshot = useSyncExternalStore(narrator.subscribe, narrator.getSnapshot, () => SERVER_SNAPSHOT);

  if (!snapshot.available) return null;

  const active = snapshot.phase !== 'idle' && snapshot.phase !== 'ended';

  return (
    <>
      <button
        type="button"
        onClick={() => (active ? narrator.stop() : narrator.start())}
        className="flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-accent-soft/60 max-sm:w-full max-sm:justify-center"
        aria-pressed={active}
      >
        {active ? <HiStop className="h-4 w-4" aria-hidden /> : <HiSpeakerWave className="h-4 w-4" aria-hidden />}
        <span>{active ? ui.narratorStop : ui.readAloud}</span>
      </button>
      {active && <NarratorBar narrator={narrator} snapshot={snapshot} ui={ui} />}
    </>
  );
}

type Ui = (typeof articleAdminUIContent)[LanguageType];

/** Keeps a bar button from taking the focus from the question field. */
const keepFocus = (event: { preventDefault: () => void }) => event.preventDefault();

function NarratorBar({ narrator, snapshot, ui }: { narrator: Narrator; snapshot: NarratorSnapshot; ui: Ui }) {
  const { phase, chunk, chunks, rate, mic, hearing, heard, answer, typing, talking, buffering, note } = snapshot;
  const progress = chunks > 0 ? Math.min(100, Math.round((chunk / chunks) * 100)) : 0;
  const [draft, setDraft] = useState('');
  const barRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pressRef = useRef<{ at: number; finishing: boolean } | null>(null);

  // The cookie banner is pinned to the same edge, above everything (z-300).
  // While it is up, the bar sits on top of it rather than under it; the
  // banner slides in and out, so its height is read on a slow tick.
  const [lift, setLift] = useState(0);
  useEffect(() => {
    const measure = () => {
      const banner = document.querySelector<HTMLElement>('[data-cookie-consent]');
      const next = banner ? Math.ceil(banner.getBoundingClientRect().height) : 0;
      setLift((current) => (current === next ? current : next));
    };
    measure();
    const timer = setInterval(measure, 500);
    return () => clearInterval(timer);
  }, []);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) return;
    narrator.submitQuestion(text);
    setDraft('');
    inputRef.current?.blur();
  };

  // Push to talk: hold to speak, let go to send. A quick tap keeps listening
  // until the listener stops speaking; a second tap sends at once.
  const onMicDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (talking) {
      pressRef.current = { at: Date.now(), finishing: true };
      return;
    }
    pressRef.current = { at: Date.now(), finishing: false };
    narrator.talkStart();
  };
  const onMicUp = (cancelled: boolean) => {
    const press = pressRef.current;
    pressRef.current = null;
    if (!press) return;
    if (press.finishing || cancelled) narrator.finishTalking();
    else narrator.talkRelease(Date.now() - press.at);
  };

  const listening = talking || (phase === 'listening' && !typing && hearing);
  const showAnswer = Boolean(answer) && (phase === 'answering' || (phase === 'listening' && !typing && !talking));

  let status: React.ReactNode = null;
  if (note === 'mic-blocked') status = <p className="text-muted">{ui.narratorMicBlocked}</p>;
  else if (note === 'answer-failed') status = <p className="text-muted">{ui.narratorAnswerFailed}</p>;
  else if (note === 'voice-failed') status = <p className="text-muted">{ui.narratorVoiceFailed}</p>;
  else if (phase === 'thinking')
    // The reader says it is looking ("Let me check the article."), so the bar only keeps the question.
    status = heard ? (
      <p className="animate-pulse text-muted">
        <span className="sr-only">{ui.narratorThinking}: </span>“{heard}”
      </p>
    ) : (
      <p className="text-muted">{ui.narratorThinking}…</p>
    );
  else if (listening)
    status = (
      <p className="flex items-start gap-2">
        <span className="mt-1.5 inline-block h-2 w-2 shrink-0 animate-pulse rounded-full bg-accent" aria-hidden />
        <span className={heard ? '' : 'text-muted'}>{heard || `${ui.narratorListening}…`}</span>
      </p>
    );
  else if (showAnswer) status = <p className="max-h-32 overflow-y-auto leading-relaxed">{answer}</p>;
  else if (buffering) status = <p className="text-xs text-muted">{ui.narratorThinking}…</p>;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-3"
      style={{ bottom: lift > 0 ? `${lift}px` : 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      <div
        ref={barRef}
        className="pointer-events-auto w-full max-w-xl overflow-hidden rounded-card border border-hairline bg-card/95 shadow-lg backdrop-blur"
      >
        <div className="h-0.5 w-full bg-hairline">
          <div className="h-full bg-accent transition-[width] duration-500" style={{ width: `${progress}%` }} />
        </div>

        <div role="status" aria-live="polite" className="px-4 pt-3 text-sm text-ink empty:hidden">
          {status}
        </div>

        <form onSubmit={submit} className="flex items-center gap-2 px-3 pt-3">
          <input
            ref={inputRef}
            value={draft}
            onFocus={() => narrator.holdForTyping()}
            onBlur={(event) => {
              const next = event.relatedTarget as Node | null;
              if (!draft.trim() && !(next && barRef.current?.contains(next))) narrator.releaseTyping();
            }}
            onChange={(event) => {
              setDraft(event.target.value);
              narrator.typingActivity();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setDraft('');
                event.currentTarget.blur();
              }
            }}
            placeholder={ui.narratorAskPlaceholder}
            aria-label={ui.narratorAskPlaceholder}
            enterKeyHint="send"
            className="min-w-0 flex-1 rounded-full border border-hairline bg-transparent px-4 py-2 text-sm text-ink outline-none placeholder:text-muted focus:border-accent"
          />
          {draft.trim() && (
            <button
              type="submit"
              onMouseDown={keepFocus}
              aria-label={ui.narratorSend}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-card"
            >
              <HiPaperAirplane className="h-4 w-4" aria-hidden />
            </button>
          )}
        </form>

        <div className="flex items-center gap-1 p-2">
          <button
            type="button"
            onMouseDown={keepFocus}
            onClick={() => narrator.togglePlay()}
            aria-label={phase === 'paused' ? ui.resume : ui.pause}
            className="flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors hover:bg-accent-soft/60"
          >
            {phase === 'paused' ? <HiPlay className="h-5 w-5" aria-hidden /> : <HiPause className="h-5 w-5" aria-hidden />}
          </button>
          <span className="min-w-[3ch] text-xs tabular-nums text-muted">{progress}%</span>
          <span className="flex-1" />
          <button
            type="button"
            onMouseDown={keepFocus}
            onClick={() => narrator.cycleRate()}
            aria-label={ui.speed}
            className="h-9 rounded-full px-3 text-xs font-medium tabular-nums text-ink transition-colors hover:bg-accent-soft/60"
          >
            {rate}×
          </button>
          {mic !== 'unsupported' && (
            <button
              type="button"
              disabled={mic === 'blocked'}
              onPointerDown={onMicDown}
              onPointerUp={() => onMicUp(false)}
              onPointerCancel={() => onMicUp(true)}
              onContextMenu={keepFocus}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                if (talking) narrator.finishTalking();
                else {
                  narrator.talkStart();
                  narrator.talkRelease(0);
                }
              }}
              aria-label={ui.narratorHoldToTalk}
              title={mic === 'blocked' ? ui.narratorMicBlocked : ui.narratorHoldToTalk}
              aria-pressed={talking}
              style={{ touchAction: 'none', WebkitTouchCallout: 'none' }}
              className={`relative flex h-11 w-11 select-none items-center justify-center rounded-full transition-colors disabled:opacity-40 ${
                talking ? 'bg-accent text-white' : 'bg-accent-soft text-ink hover:bg-accent-soft/80'
              }`}
            >
              {talking && <span className="absolute inset-0 animate-ping rounded-full bg-accent/30" aria-hidden />}
              <HiMicrophone className="relative h-5 w-5" aria-hidden />
            </button>
          )}
          <button
            type="button"
            onMouseDown={keepFocus}
            onClick={() => (phase === 'listening' || phase === 'thinking' ? narrator.cancelAsk() : narrator.stop())}
            aria-label={phase === 'listening' || phase === 'thinking' ? ui.cancel : ui.narratorStop}
            className="flex h-10 w-10 items-center justify-center rounded-full text-muted transition-colors hover:bg-accent-soft/60 hover:text-ink"
          >
            <HiXMark className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}

export default ArticleNarrator;
